/**
 * The rule behind the Mixpanel super property `juiscigpt_onboarding`
 * (`hooks/use-mixpanel.ts`),
 * documented in `services/analytics/AGENTS.md`.
 *
 * A reader is onboarded in an ISO week (Monday to Sunday, Europe/Paris) when
 * their first JuisciGPT question was asked in an EARLIER week. The week of the
 * first question does not count: the reader becomes onboarded on the next
 * Monday at 00:00 Europe/Paris.
 */
export function isJuisciGptOnboarded(
  firstQuestionAt: Date | null,
  now: Date
): boolean {
  if (firstQuestionAt === null) return false;
  return firstQuestionAt.getTime() < startOfParisIsoWeek(now).getTime();
}

const PARIS = "Europe/Paris";

const ISO_WEEKDAY = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
} as const;

const parisParts = new Intl.DateTimeFormat("en-US", {
  timeZone: PARIS,
  hourCycle: "h23",
  weekday: "short",
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
});

function readParis(instant: Date) {
  const parts = parisParts.formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes) => {
    const found = parts.find((p) => p.type === type);
    if (found === undefined)
      throw new Error(`should not happen: no ${type} in a Paris date`);
    return found.value;
  };
  const weekday = part("weekday");
  switch (weekday) {
    case "Mon":
    case "Tue":
    case "Wed":
    case "Thu":
    case "Fri":
    case "Sat":
    case "Sun":
      return {
        isoWeekday: ISO_WEEKDAY[weekday],
        year: Number(part("year")),
        month: Number(part("month")),
        day: Number(part("day")),
        hour: Number(part("hour")),
        minute: Number(part("minute")),
        second: Number(part("second")),
      };
    default:
      throw new Error(`should not happen: weekday ${weekday}`);
  }
}

/** Paris wall-clock time minus UTC at this instant, in milliseconds. */
function parisOffsetMs(instant: Date): number {
  const wall = readParis(instant);
  const wallAsUtc = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second
  );
  return wallAsUtc - (instant.getTime() - instant.getMilliseconds());
}

/** Monday 00:00 Europe/Paris of the ISO week that holds `now`. */
export function startOfParisIsoWeek(now: Date): Date {
  const today = readParis(now);
  // Midnight of that Monday, read as if Paris were UTC. Date.UTC normalizes
  // a day before the 1st into the previous month.
  const mondayAsUtc = Date.UTC(
    today.year,
    today.month - 1,
    today.day - (today.isoWeekday - 1)
  );
  // Paris changes its offset on a Sunday at 01:00 UTC. Monday 00:00 UTC is
  // thus on the same side of a change as Monday 00:00 Paris, and has the same
  // offset.
  return new Date(mondayAsUtc - parisOffsetMs(new Date(mondayAsUtc)));
}
